import { type Locale, resolveExecutionModeV1 } from "@adpilot/shared";
import { IconSettings, IconSparkles } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AiRunHistoryTable } from "@/components/ai-operations/AiRunHistoryTable";
import { PendingApprovalsSection } from "@/components/ai-operations/PendingApprovalsSection";
import { runEligibleGate } from "@/components/ai-operations/run-eligible-gate";
import { ControlButton } from "@/components/patterns/control-button";
import { PageHeader } from "@/components/patterns/page-header";
import { ReportingFilters } from "@/components/patterns/reporting-filters";
import { UnavailableControl } from "@/components/patterns/unavailable-control";
import { Badge } from "@/components/ui/badge";
import { usePermission } from "@/hooks/use-permission";
import { useShopScope } from "@/hooks/use-shop-scope";
import {
  aiOperationsRefetchInterval,
  fetchAiOperationsRuns,
  triggerAiRunsBulk,
} from "@/lib/ai-run-api";
import { getApiErrorMessage } from "@/lib/api-error";
import { parseApprovalSearch } from "@/lib/approval-search";
import { fetchDashboardSummary } from "@/lib/dashboard-api";
import { useFormatters } from "@/lib/format";
import { requireModule } from "@/lib/module-access";
import { fetchSystemAiPolicy } from "@/lib/settings-api";
import { formatBeijingDate, formatBeijingTime, isBeijingDayOffset } from "@/lib/time";
import { cn } from "@/lib/utils";
import { WorkspaceAiPolicyDialog } from "@/routes/_authed/index";
import { useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

export const Route = createFileRoute("/_authed/ai-operations")({
  beforeLoad: () => {
    requireModule("approvals");
  },
  validateSearch: parseApprovalSearch,
  component: AiOperationsPage,
});

function AiOperationsPage(): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { hasPermission } = usePermission();
  const canManage = hasPermission("manage_ai");
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const range = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  // ADP-110: the analytics scope (multi-select, default all workspace shops)
  // drives the aggregated run history; the sorted set keys the queries.
  const { scopeShopIds } = useShopScope();
  const scopeKey = scopeShopIds.join(",");
  const aggregate = scopeShopIds.length > 1;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pendingBatchId, setPendingBatchId] = useState<string | null>(null);
  const runsQuery = useQuery({
    queryKey: ["ai-operations", "runs", scopeKey, range, timeZone],
    queryFn: () => fetchAiOperationsRuns(range, timeZone, scopeShopIds),
    refetchInterval: (query) => aiOperationsRefetchInterval(query.state.data, pendingBatchId),
  });
  const dashboardQuery = useQuery({
    queryKey: ["dashboard", "summary", scopeKey, range, timeZone],
    queryFn: () => fetchDashboardSummary(range, timeZone, scopeShopIds),
  });
  const policyQuery = useQuery({
    queryKey: ["settings", "ai-policy"],
    queryFn: fetchSystemAiPolicy,
  });
  // Why the manual run is blocked (AI master switch off, aggregated scope).
  // A disabled button cannot show a tooltip, so the reason wraps it below.
  const runGate = runEligibleGate({ aiMasterOn: dashboardQuery.data?.aiMasterOn, aggregate });
  const runMutation = useMutation({
    mutationFn: async () => {
      const campaignIds =
        dashboardQuery.data?.campaigns
          .filter((campaign) => campaign.bidAiEligible && campaign.aiManaged)
          .map((campaign) => campaign.id) ?? [];
      if (campaignIds.length === 0) return { total: 0, queued: 0, batchId: null };
      const result = await triggerAiRunsBulk(campaignIds);
      return { total: campaignIds.length, queued: result.queued, batchId: result.batchId };
    },
    onSuccess: ({ total, queued, batchId }) => {
      if (queued > 0) setPendingBatchId(batchId);
      void runsQuery.refetch();
      if (total === 0) toast.info(t("toast.noEligible"));
      else if (queued === total) toast.success(t("toast.queued", { queued }));
      else toast.warning(t("toast.partial", { queued, total }));
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });

  useEffect(() => {
    if (!pendingBatchId) return;
    if (runsQuery.data?.some((run) => run.batchId === pendingBatchId)) {
      setPendingBatchId(null);
    }
  }, [pendingBatchId, runsQuery.data]);

  useEffect(() => {
    if (!pendingBatchId) return;
    const timeoutId = window.setTimeout(() => {
      setPendingBatchId((current) => (current === pendingBatchId ? null : current));
    }, 60_000);
    return () => window.clearTimeout(timeoutId);
  }, [pendingBatchId]);

  return (
    <main className="flex min-h-full min-w-0 flex-col gap-3.5 px-6 pb-6 pt-5">
      <PageHeader
        filters={<ReportingFilters />}
        title={t("page.title")}
        description={t("page.description")}
        actions={
          canManage ? (
            <>
              <ControlButton
                controlSize="toolbar"
                variant="outline"
                onClick={() => setSettingsOpen(true)}
              >
                <IconSettings data-icon="inline-start" />
                {t("page.editSettings")}
              </ControlButton>
              {runGate.reasonKey ? (
                <UnavailableControl reason={t(runGate.reasonKey)}>
                  <IconSparkles data-icon="inline-start" />
                  {t("page.runEligible")}
                </UnavailableControl>
              ) : (
                <ControlButton
                  controlSize="toolbar"
                  loading={runMutation.isPending}
                  disabled={runGate.disabled}
                  onClick={() => runMutation.mutate()}
                >
                  <IconSparkles data-icon="inline-start" />
                  {t("page.runEligible")}
                </ControlButton>
              )}
            </>
          ) : null
        }
      />

      <PolicySummary dashboard={dashboardQuery.data} policy={policyQuery.data} />

      <PendingApprovalsSection managedGroupId={search.managedGroupId} />

      <AiRunHistoryTable
        runs={runsQuery.data ?? []}
        isLoading={runsQuery.isLoading}
        isError={runsQuery.isError}
        onRetry={() => void runsQuery.refetch()}
        onOpenRun={(runId) => void navigate({ to: "/ai-operations/$runId", params: { runId } })}
        initialManagedGroupId={search.managedGroupId}
      />
      <WorkspaceAiPolicyDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        eligibleCampaigns={dashboardQuery.data?.aiSummary.eligibleCampaigns}
        excludedCampaigns={
          dashboardQuery.data
            ? Math.max(
                0,
                dashboardQuery.data.campaigns.length -
                  dashboardQuery.data.aiSummary.eligibleCampaigns,
              )
            : undefined
        }
        nextRunAt={dashboardQuery.data?.aiSummary.nextRunAt}
      />
    </main>
  );
}

function PolicySummary({
  dashboard,
  policy,
}: {
  dashboard: Awaited<ReturnType<typeof fetchDashboardSummary>> | undefined;
  policy: Awaited<ReturnType<typeof fetchSystemAiPolicy>> | undefined;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { locale } = useFormatters();
  const pendingDecisions = dashboard?.aiSummary.pendingDecisions ?? 0;
  const eligibleCampaigns = dashboard?.aiSummary.eligibleCampaigns ?? 0;
  const enabled = policy?.systemEnabled ?? false;
  const goal = policy
    ? t(`policy.goal.${policy.policy.businessGoal}`, {
        defaultValue: formatBusinessGoal(policy.policy.businessGoal),
      })
    : "—";
  const executionMode = policy ? resolveExecutionModeV1(policy.policy) : null;
  const mode =
    executionMode === null
      ? "—"
      : executionMode === "recommendation"
        ? t("policy.mode.recommendation")
        : executionMode === "full-auto"
          ? t("policy.mode.fullAuto")
          : t("policy.mode.semiAuto");

  return (
    <section
      aria-label={t("policy.ariaLabel")}
      className="flex h-[92px] shrink-0 overflow-hidden rounded-lg border border-line bg-surface"
    >
      <div className="flex w-[278px] shrink-0 flex-col justify-center gap-[5px] border-r border-line px-4">
        <div className="flex items-center gap-[7px]">
          <h2 className="text-[13px] font-semibold leading-[18px] text-ink">{t("policy.title")}</h2>
          <Badge variant={enabled ? "success" : "neutral"} className="font-semibold">
            <span className="size-1.5 rounded-full bg-current" aria-hidden />
            {enabled ? t("policy.active") : t("policy.notEnabled")}
          </Badge>
        </div>
        <p className="text-[10px] leading-3.5 text-muted-foreground">
          {t("policy.inheritCount", { eligible: eligibleCampaigns })}
        </p>
      </div>
      <PolicyMetric label={t("policy.businessGoal")} value={goal} className="w-[206px]" />
      <PolicyMetric
        label={t("policy.targetAcos")}
        value={policy ? `${Math.round(policy.policy.targetAcos * 100)}%` : "—"}
        emphasis
        className="w-[148px]"
      />
      <PolicyMetric label={t("policy.executionMode")} value={mode} className="w-[170px]" />
      <PolicyMetric
        label={t("policy.nextRun")}
        value={formatNextRun(dashboard?.aiSummary.nextRunAt, locale, t)}
        className="w-[190px]"
      />
      <div className="flex min-w-[164px] flex-1 flex-col justify-center gap-[5px] px-[15px]">
        <span className="text-[10px] uppercase leading-[13px] text-muted-foreground">
          {t("policy.humanAttention")}
        </span>
        <span className="flex items-baseline gap-[5px]">
          <strong
            className={cn(
              "text-[17px] leading-5",
              pendingDecisions > 0 ? "text-amber-11" : "text-ink",
            )}
          >
            {pendingDecisions}
          </strong>
          <span className="text-[10px] leading-3.5 text-muted-foreground">{t("policy.plans")}</span>
        </span>
      </div>
    </section>
  );
}

function PolicyMetric({
  label,
  value,
  emphasis = false,
  className,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  className: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "flex shrink-0 flex-col justify-center gap-[5px] border-r border-line px-[15px]",
        className,
      )}
    >
      <span className="text-[10px] uppercase leading-[13px] text-muted-foreground">{label}</span>
      <strong
        className={
          emphasis
            ? "truncate text-[17px] font-bold leading-5 tracking-[-0.01em] text-ink"
            : "truncate text-xs font-semibold leading-4 text-ink"
        }
      >
        {value}
      </strong>
    </div>
  );
}

function formatBusinessGoal(goal: string): string {
  const value = goal.replaceAll("_", " ").toLowerCase();
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// 展示北京时间的具体日期 + 时刻,明天时仍显示「明天」。不按星期几定位,
// 这样 cron 周期变化(ADP-127 曾为每 3 天,现为每天)或运行延迟时都能准确表达。
function formatNextRun(
  value: string | null | undefined,
  locale: Locale,
  t: (key: string) => string,
): string {
  if (!value) return "—";
  const label = isBeijingDayOffset(value, 1)
    ? t("policy.tomorrow")
    : formatBeijingDate(value, locale);
  return `${label} · ${formatBeijingTime(value)}`;
}
